import React, { useEffect, useRef, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { StylePersonalLabelBank } from "../AdminPaymentChannelNumber/styled/PaymentNumber";
import DialogWrap, { DialogWrapMedium } from "../../controls/components/dialogWrap/DialogWrap";
import { UserFormServiceButtonClose, UserFormServiceButtonCreated } from "../MyBooking/styled/DesStyled";
import useContent from "./hook/useContent";
import useUpdateContent from "./hook/useUpdateContent";
import { TextField } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import { getTrustItems, updateTrustItems } from "../../../services/UserService";
import useAlertDialog from "../../hooks/useAlertDialog";
import { AlertType } from "../../hooks/common/useAttachAlertDialog";
import { getAdminContentLabel } from "./contentConfig";
import PermissionGate from "../../controls/auth/PermissionGate";

export default function DialogAddOrderAdmin(props: {
	openDialog: boolean;
	setOpenDialog: (value: boolean) => void;
	dialogType: string;
}) {
	const { setContent, content } = useContent({ dialogType: props.dialogType, openDialog: props.openDialog });
	const editorRef = useRef<any>(null);
	const { doUpdate } = useUpdateContent({ dialogType: props.dialogType, setOpenDialog: props.setOpenDialog });
	const { openAlertDialog } = useAlertDialog();
	const [trustItems, setTrustItems] = useState<any[]>([{ text: "" }]);

	useEffect(() => {
		if (props.openDialog && props.dialogType === "trust_items") {
			loadTrustItems();
		}
	}, [props.openDialog, props.dialogType]);

	const loadTrustItems = async () => {
		try {
			const result = await getTrustItems();
			setTrustItems(result.trustItems.length > 0 ? result.trustItems : [{ text: "" }]);
		} catch (err) {
			console.error("Error loading trust items:", err);
		}
	};

	const handleSave = async () => {
		if (props.dialogType === "trust_items") {
			try {
				await updateTrustItems(trustItems);
				openAlertDialog?.(AlertType.Success, "Updated trust items for all stations successfully!", () => {});
				props.setOpenDialog(false);
			} catch (err: any) {
				console.error("Error updating trust items:", err);
				openAlertDialog?.(AlertType.Fail, err?.response?.data?.message || "Failed to update trust items");
			}
		} else {
			const editorContent = editorRef.current?.getContent?.() || " ";
			await doUpdate(editorContent);
			props.setOpenDialog(false);
		}
	};

	return (
		<DialogWrap
			login={true}
			disableEnforceFocus={true}
			disableAutoFocus={true}
			disableRestoreFocus={true}
			open={props.openDialog}
			onClose={() => {
				props.setOpenDialog(false);
			}}
			title={
				props.dialogType === "trust_items"
					? "Trust Items (All Stations)"
					: getAdminContentLabel(props.dialogType)
			}
			actions={
				<>
					<UserFormServiceButtonClose onClick={() => props.setOpenDialog(false)}>
						Cancel
					</UserFormServiceButtonClose>
					<PermissionGate permission="documents.manage">
						<UserFormServiceButtonCreated onClick={handleSave}>Save</UserFormServiceButtonCreated>
					</PermissionGate>
				</>
			}>
			{props.dialogType === "trust_items" ? (
				<TrustItemsManagement
					trustItems={trustItems}
					setTrustItems={setTrustItems}
				/>
			) : (
				<AdminContentBlogManagement
					dialogType={props.dialogType}
					content={content}
					setContent={setContent}
					editorRef={editorRef}
				/>
			)}
		</DialogWrap>
	);
}

export function AdminContentBlogManagement(props: {
	dialogType: string;
	content: string;
	setContent: (val: string) => void;
	editorRef: React.MutableRefObject<any>;
}) {
	const [isEditorReady, setIsEditorReady] = useState(false);

	useEffect(() => {
		let cancelled = false;
		if (typeof window === "undefined") return;

		(async () => {
			try {
				await import("tinymce/tinymce");
				await import("tinymce/icons/default");
				await import("tinymce/themes/silver");
				await import("tinymce/models/dom");

				await import("tinymce/plugins/advlist");
				await import("tinymce/plugins/autolink");
				await import("tinymce/plugins/lists");
				await import("tinymce/plugins/link");
				await import("tinymce/plugins/image");
				await import("tinymce/plugins/charmap");
				await import("tinymce/plugins/preview");
				await import("tinymce/plugins/anchor");
				await import("tinymce/plugins/searchreplace");
				await import("tinymce/plugins/visualblocks");
				await import("tinymce/plugins/code");
				await import("tinymce/plugins/fullscreen");
				await import("tinymce/plugins/insertdatetime");
				await import("tinymce/plugins/media");
				await import("tinymce/plugins/table");
				await import("tinymce/plugins/wordcount");

				await import("tinymce/skins/ui/oxide/skin.min.css");
				await import("tinymce/skins/content/default/content.min.css");

				if (!cancelled) setIsEditorReady(true);
			} catch (err) {
				console.error("Failed to load TinyMCE locally:", err);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	const customImageAltPlugin = (editor: any) => {
		editor.ui.registry.addButton("altimage", {
			text: "+ văn bản thay thế",
			icon: null,
			onAction: function () {
				const selectedImage = editor.selection.getNode();
				const currentAltText = selectedImage.alt || "";
				const altText = prompt("Thêm văn bản thay thế cho ảnh:", currentAltText);
				if (altText !== null && selectedImage.nodeName === "IMG") {
					selectedImage.alt = altText;
				}
			},
		});

		editor.ui.registry.getAll().icons;
	};

	return (
		<div id="tinymce-ui-container">
			<StylePersonalLabelBank>Nội dung</StylePersonalLabelBank>
			{isEditorReady ? (
				<Editor
					key={props.dialogType}
					licenseKey="gpl"
					onInit={(evt: any, editor: any) => {
						props.editorRef.current = editor;
						customImageAltPlugin(editor);
					}}
					value={props.content}
					onEditorChange={(nextValue: string) => props.setContent(nextValue)}
					init={{
						height: 500,
						ui_container: "#tinymce-ui-container",
						menubar: "file edit view insert format tools table",
						toolbar_mode: "wrap",
						plugins:
							"advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table wordcount",
						toolbar:
							"undo redo | blocks | bold italic | forecolor backcolor | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist | link unlink | image media | code visualblocks fullscreen | removeformat | altimage",
						skin: false,
						content_css: false,
						automatic_uploads: true,
						file_picker_types: "image",
						setup: (editor: any) => {
							editor.on("PastePostProcess", (e: any) => {
								const images = e.node.querySelectorAll("img");
								images.forEach((img: any) => {
									editor.dom.setAttrib(img, "width", "200");
								});
							});
						},
					}}
				/>
			) : (
				<div style={{ padding: 12 }}>Loading editor...</div>
			)}
		</div>
	);
}

export function TrustItemsManagement(props: { trustItems: any[]; setTrustItems: (items: any[]) => void }) {
	const handleAddItem = () => {
		props.setTrustItems([...props.trustItems, { text: "" }]);
	};

	const handleRemoveItem = (index: number) => {
		const newItems = [...props.trustItems];
		newItems.splice(index, 1);
		props.setTrustItems(newItems.length > 0 ? newItems : [{ text: "" }]);
	};

	const handleChange = (index: number, value: string) => {
		const newItems = [...props.trustItems];
		newItems[index] = { ...newItems[index], text: value };
		props.setTrustItems(newItems);
	};

	return (
		<div>
			<div
				style={{
					marginBottom: "16px",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
				}}>
				<strong style={{ fontSize: "16px" }}>Trust Items (Applied to all stations)</strong>
				<PermissionGate permission="documents.manage">
					<button
						onClick={handleAddItem}
						style={{
							padding: "8px 16px",
							background: "#2563EB",
							color: "white",
							border: "none",
							borderRadius: "4px",
							cursor: "pointer",
							fontSize: "14px",
						}}>
						+ Add Item
					</button>
				</PermissionGate>
			</div>
			<div style={{ maxHeight: "400px", overflowY: "auto", paddingRight: "10px" }}>
				{props.trustItems.map((item, index) => (
					<div
						key={index}
						style={{
							border: "1px solid #e0e0e0",
							borderRadius: "8px",
							padding: "16px",
							marginBottom: "16px",
							position: "relative",
						}}>
						<PermissionGate permission="documents.manage">
							<IconButton
								onClick={() => handleRemoveItem(index)}
								style={{
									position: "absolute",
									top: "8px",
									right: "8px",
									background: "#ef4444",
									color: "white",
									width: "24px",
									height: "24px",
									padding: "0",
								}}
								size="small">
								×
							</IconButton>
						</PermissionGate>
						<TextField
							label={`Item ${index + 1}`}
							variant="outlined"
							fullWidth
							value={item.text}
							onChange={(e) => handleChange(index, e.target.value)}
							style={{ marginBottom: "8px" }}
							placeholder="e.g., 200+ WhaleLO Stations Globally"
						/>
					</div>
				))}
			</div>
		</div>
	);
}
